import React from "react";
import { QTYPES, CHOICE_TYPES } from "@/lib/formTypes";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, ArrowUp, ArrowDown, Plus, X, Copy } from "lucide-react";

const IconBtn = ({ onClick, children, testid, title }) => (
  <button type="button" onClick={onClick} data-testid={testid} title={title}
    className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">{children}</button>
);

function OptionsEditor({ q, idx, onChange }) {
  const setOpt = (i, v) => onChange({ options: q.options.map((o, j) => (j === i ? v : o)) });
  return (
    <div className="space-y-1.5 mt-3">
      {q.options.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className={`h-4 w-4 border-2 border-slate-300 ${q.type === "checkbox" ? "rounded" : "rounded-full"}`} />
          <Input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Opsi ${i + 1}`} className="h-9" data-testid={`q-${idx}-opt-${i}`} />
          {q.options.length > 1 && <IconBtn onClick={() => onChange({ options: q.options.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></IconBtn>}
        </div>
      ))}
      <button type="button" onClick={() => onChange({ options: [...q.options, ""] })} data-testid={`q-${idx}-add-opt`}
        className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 mt-1"><Plus className="h-4 w-4" />Tambah opsi</button>
    </div>
  );
}

export function QuestionEditor({ q, idx, total, onChange, onRemove, onMove, onDuplicate }) {
  const changeType = (t) => onChange({ type: t, options: CHOICE_TYPES.includes(t) ? (q.options?.length ? q.options : ["Opsi 1"]) : [] });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 border-l-4 border-l-indigo-400" data-testid={`question-${idx}`}>
      <div className="flex gap-3 flex-col sm:flex-row">
        <Input value={q.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Pertanyaan" className="font-semibold flex-1" data-testid={`q-${idx}-label`} />
        <Select value={q.type} onValueChange={changeType}>
          <SelectTrigger className="sm:w-48" data-testid={`q-${idx}-type`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {QTYPES.map((t) => <SelectItem key={t.key} value={t.key}><span className="inline-flex items-center gap-2"><t.icon className="h-4 w-4 text-slate-500" />{t.label}</span></SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Input value={q.description || ""} onChange={(e) => onChange({ description: e.target.value })} placeholder="Keterangan (opsional)" className="mt-2 h-9 text-sm" />
      {CHOICE_TYPES.includes(q.type) && <OptionsEditor q={q} idx={idx} onChange={onChange} />}
      {q.type === "scale" && <p className="text-xs text-slate-400 mt-3">Responden memilih nilai 1 (rendah) – 5 (tinggi).</p>}
      {q.type === "file" && <p className="text-xs text-slate-400 mt-3">Responden dapat mengunggah foto / dokumen (maks 10 MB per file).</p>}
      <div className="flex items-center justify-end gap-1 mt-4 pt-3 border-t border-slate-100">
        <IconBtn onClick={() => onMove(-1)} testid={`q-${idx}-up`} title="Naik">{idx > 0 && <ArrowUp className="h-4 w-4" />}</IconBtn>
        <IconBtn onClick={() => onMove(1)} testid={`q-${idx}-down`} title="Turun">{idx < total - 1 && <ArrowDown className="h-4 w-4" />}</IconBtn>
        <IconBtn onClick={onDuplicate} testid={`q-${idx}-dup`} title="Duplikat"><Copy className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={onRemove} testid={`q-${idx}-remove`} title="Hapus"><Trash2 className="h-4 w-4" /></IconBtn>
        <span className="mx-2 h-5 w-px bg-slate-200" />
        <label className="text-sm text-slate-600 inline-flex items-center gap-2">Wajib<Switch checked={q.required} onCheckedChange={(v) => onChange({ required: v })} data-testid={`q-${idx}-required`} /></label>
      </div>
    </div>
  );
}
